"use strict";
/**
 * Audio Format Standard — single source of truth for the EduWrite platform.
 *
 * MP3 is the only format stored for playback. All other accepted input
 * formats (M4A, WAV, OGG, MP4, WebM) are transcoded to MP3 on upload
 * (server-side, via ffmpeg). See WP-055 for the full decision rationale.
 *
 * Both sync-server (Config.ts) and student-app (useAudioPlayback) import
 * from this file so the format standard is never duplicated.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MP3_MIME_TYPES = exports.ACCEPTED_INPUT_MIME_TYPES = exports.AUDIO_ENCODING = exports.AUDIO_STANDARD_EXTENSION = exports.AUDIO_STANDARD_MIME_TYPE = exports.AUDIO_STANDARD_FORMAT = void 0;
exports.classifyAudioError = classifyAudioError;
exports.isMp3MimeType = isMp3MimeType;
/** The single storage and serving format. */
exports.AUDIO_STANDARD_FORMAT = 'mp3';
/** MIME type for the standard format. */
exports.AUDIO_STANDARD_MIME_TYPE = 'audio/mpeg';
/** File extension for the standard format (without the dot). */
exports.AUDIO_STANDARD_EXTENSION = 'mp3';
/** Encoding parameters for speech-quality MP3. */
exports.AUDIO_ENCODING = {
    /** Bitrate in kbps — 64kbps is sufficient for speech clarity. */
    bitrate: 64,
    /** Sample rate in Hz — 44.1kHz is universally supported. */
    sampleRate: 44100,
    /** Channel layout — mono is sufficient for speech and halves file size. */
    channels: 'mono',
};
/**
 * MIME types accepted as upload inputs. Non-MP3 inputs are transcoded
 * to MP3 before storage. MP3 inputs are stored as-is (pass-through).
 */
exports.ACCEPTED_INPUT_MIME_TYPES = [
    'audio/mpeg',
    'audio/mp3',
    'audio/m4a',
    'audio/wav',
    'audio/ogg',
    'audio/mp4',
    'audio/webm',
];
/**
 * MIME types that are already MP3 and do NOT need transcoding.
 * `audio/mpeg` and `audio/mp3` are both MP3.
 */
exports.MP3_MIME_TYPES = ['audio/mpeg', 'audio/mp3'];
/**
 * Classifies a raw error from AudioService.play into a typed
 * AudioPlaybackError. This enables the UI to show appropriate
 * fallback messaging (e.g. "Audio unavailable" vs "Audio not found").
 */
function classifyAudioError(err) {
    const message = err instanceof Error ? err.message : String(err);
    const lower = message.toLowerCase();
    if (lower.includes('404') || lower.includes('not found') || lower.includes('not_found')) {
        return { type: 'not_found', message, cause: err };
    }
    if (lower.includes('network') || lower.includes('fetch') || lower.includes('timeout') || lower.includes('econn')) {
        return { type: 'network', message, cause: err };
    }
    if (lower.includes('codec') || lower.includes('format') || lower.includes('unsupported') || lower.includes('decode')) {
        return { type: 'codec', message, cause: err };
    }
    return { type: 'unknown', message, cause: err };
}
/**
 * Returns true if the given MIME type is already MP3 (no transcoding needed).
 */
function isMp3MimeType(mimeType) {
    return exports.MP3_MIME_TYPES.includes(mimeType);
}
